import { MaybeNil } from '../../../utils/interfaces';
import { IDateInput } from '../../../redux/slices/oldBuilder/date/types';
import styled from 'styled-components';
import DateRangeDisplay from './DateRangeDisplay';
import WidgetContainer from './WidgetContainer';
import {
  IDatePickerFieldsFragment,
  IDateRangeFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { GraphQLDate } from '../../../utils/types';
import { isNil } from 'lodash';
import { forwardRef, useState } from 'react';
import { Loader } from '../../Loaders';
import useCalculateDateInputEffect from './hooks/useCalculateDateInputEffect';
import { SquareRemoveButton } from '../../Button';
import {
  KubitPopover,
  KubitPopoverContent,
  KubitPopoverTrigger,
} from '../../KubitPopover';

interface IProps {
  /**
   * Schema ID is used to calculate relative time periods according to the
   * schema's week start. We should strive to pass it all the time in order
   * to set the right expectations for the user. Very occasionally
   * we don't have concept of schema in the place where we use it (e.g.
   * Incidents search) thus we allow passing null as an exception.
   * In this case, Kobol calculates the absolute time period based on the
   * default schema of the client
   */
  schemaId: MaybeNil<string>;
  endDate: GraphQLDate;
  datePicker: MaybeNil<IDatePickerFieldsFragment>;
  defaultDatePicker: IDatePickerFieldsFragment;
  dateRange: MaybeNil<IDateRangeFieldsFragment>;
  onChange: (newDateInput: IDateInput | null) => void;
  onUpdateDateRange?: (newDateRange: IDateRangeFieldsFragment | null) => void;
}

const Container = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const OptionalDatePickerWidget = forwardRef<HTMLDivElement, IProps>(
  (
    {
      schemaId,
      datePicker,
      dateRange,
      defaultDatePicker,
      onChange,
      onUpdateDateRange,
      endDate,
    },
    ref,
  ) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const clearDateRange = () => onChange(null);

    const handleConfirm = (dateInput: IDateInput) => {
      setIsPopoverOpen(false);
      onChange(dateInput);
    };

    const calculatedDateInputs = useCalculateDateInputEffect(
      schemaId,
      datePicker,
      dateRange,
      defaultDatePicker,
      endDate,
      isPopoverOpen,
      (dateInput) => {
        if (!isNil(onUpdateDateRange)) {
          onUpdateDateRange({
            __typename: 'DateRange',
            start: dateInput.startDate,
            end: dateInput.endDate,
          });
        } else {
          onChange(dateInput);
        }
      },
    );

    if (!calculatedDateInputs.available) {
      return <Loader />;
    }

    const { dateInput, defaultDateInput } = calculatedDateInputs;
    const widgetDateInput = dateInput ?? defaultDateInput;

    return (
      <Container>
        <KubitPopover isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <KubitPopoverTrigger asChild={true}>
            <div ref={ref}>
              <DateRangeDisplay dateInput={dateInput} />
            </div>
          </KubitPopoverTrigger>
          <KubitPopoverContent>
            {!isNil(widgetDateInput) && (
              <WidgetContainer
                endDate={endDate}
                onConfirm={handleConfirm}
                dateInput={widgetDateInput}
                schemaId={schemaId}
              />
            )}
          </KubitPopoverContent>
        </KubitPopover>
        {!isNil(dateInput) && (
          <SquareRemoveButton
            onClick={clearDateRange}
            content={'Remove Date Range'}
          />
        )}
      </Container>
    );
  },
);

export default OptionalDatePickerWidget;
